import { z } from "zod"
import { Discount } from "./discount"
import { JobStatus } from "./job"
import { ServiceAction } from "./service-part-action"

export type VatRate = 12 | 25

export interface ReceiptProduct {
  id: string
  externalId: string | null
  ean: string | null
  articleNumber: string | null
  acquisitionCost: number | null
  title: string
  price: number
  quantity: number
  discount?: Discount | null
}

export interface ReceiptPartAction {
  partId: string
  /**
   * Kept out of the job's labour, products and order discounts, by the
   * discount code's exclusions. See `PriceUtils.isExcludedFromJobDiscounts`.
   */
  isExcludedFromJobDiscounts?: boolean
  partName: string
  partAction: ServiceAction
  /** Optional custom label for the action (e.g., for damper service custom names) */
  actionLabel?: string
  /** Free-text comment written by the mechanic during reception, inspection, or addon order. */
  comment?: string
  products: ReceiptProduct[]
  /**
   * The effective labor price used in totalCost calculation.
   * This is the discounted price if a discount was applied, otherwise the original price.
   * @deprecated Use laborPriceOriginal and laborPriceAfterDiscount for clarity
   */
  laborPrice: number
  /** The original labor price before any discount */
  laborPriceOriginal: number
  /** The labor price after discount, or null if no discount was applied */
  laborPriceAfterDiscount: number | null
  /** The discount (percent or kr), or null if no discount */
  laborPriceDiscount: Discount | null
  totalProductCost: number
  totalCost: number
  vatRate?: VatRate
  /**
   * Human-readable explanation for why this item has 25% VAT instead of the
   * default 12%. Only set when vatRate is 25.
   */
  vat25Reason?: string
}

export interface ReceiptManualAction {
  id: string
  title: string
  /**
   * Bought in the proposal's "Tillval" step. Its price already carries the
   * store's "here and now" discount, so job-level discounts leave it alone.
   */
  isUpsell?: boolean
  /**
   * Kept out of the job's labour, products and order discounts, by the
   * discount code's exclusions. See `PriceUtils.isExcludedFromJobDiscounts`.
   */
  isExcludedFromJobDiscounts?: boolean
  /** Free-text comment written by the mechanic when creating the manual action. */
  comment?: string
  products: ReceiptProduct[]
  /**
   * The effective labor price used in totalCost calculation.
   * This is the discounted price if a discount was applied, otherwise the original price.
   * @deprecated Use laborPriceOriginal and laborPriceAfterDiscount for clarity
   */
  laborPrice: number
  /** The original labor price before any discount */
  laborPriceOriginal: number
  /** The labor price after discount, or null if no discount was applied */
  laborPriceAfterDiscount: number | null
  /** The discount (percent or kr), or null if no discount */
  laborPriceDiscount: Discount | null
  totalProductCost: number
  totalCost: number
  vatRate?: VatRate
  /**
   * Human-readable explanation for why this item has 25% VAT instead of the
   * default 12%. Only set when vatRate is 25.
   */
  vat25Reason?: string
}

export interface ReceiptPackagePartAction {
  partId: string
  partName: string
  partAction: ServiceAction
  /** Optional custom label for the action (e.g., for damper service custom names) */
  actionLabel?: string
}

export interface ReceiptPackageAvailableAction {
  partId: string
  partName: string
  action: ServiceAction
}

export interface ReceiptPackageProduct
  extends ReceiptProduct,
    ReceiptPackagePartAction {}

export interface ReceiptWash {
  snapshotId: string
  title: string
  /** The effective price used in totalCost calculation, after `priceDiscount`. */
  price: number
  /** The original price before any discount */
  priceOriginal: number
  /** The discount (percent or kr), or null if no discount */
  priceDiscount: Discount | null
  vatRate?: VatRate
}

/**
 * A wash covered by a package. Priceless by design — the package price already
 * includes it. Keeps `originalWashId` so the receipt editor can rebuild a row
 * for it and re-attach it to the same package on save.
 */
export interface ReceiptPackageWash
  extends Omit<ReceiptWash, "price" | "priceOriginal" | "priceDiscount"> {
  originalWashId: string
}

export interface ReceiptPackage {
  originalPackageId: string
  /**
   * Kept out of the job's labour, products and order discounts, by the
   * discount code's exclusions. See `PriceUtils.isExcludedFromJobDiscounts`.
   */
  isExcludedFromJobDiscounts?: boolean
  snapshotId: string
  title: string
  wash: ReceiptPackageWash | null
  partActions: ReceiptPackagePartAction[]
  availableActions: ReceiptPackageAvailableAction[]
  products: ReceiptPackageProduct[]
  /** The original package labor price before any discount */
  laborPrice: number
  /** The labor price after discount, or null if no discount was applied */
  laborPriceAfterDiscount: number | null
  /** The discount (percent or kr), or null if no discount */
  laborPriceDiscount: Discount | null
  totalProductCost: number
  /**
   * The material allowance configured for this package's matched price entry.
   * Null or 0 means no material is included. A fixed kr amount.
   */
  includedMaterialCost: number | null
  /**
   * Kr of the package labor replaced by included material:
   * min(totalProductCost, includedMaterialCost, labor after per-package
   * discount). The package price covers that material, so this share of the
   * price counts as products instead of labor. Capped at the labor, so with a
   * large per-package discount the total can exceed the discounted package
   * price (see `PriceUtils.getMaterialLaborDeduction`). Use
   * `PriceUtils.getPackageLaborPrice` for the labor the package charges.
   */
  materialLaborDeduction: number
  /**
   * Total cost: the package labor (after per-package discount and
   * `materialLaborDeduction`) plus product costs.
   */
  totalCost: number
  vatRate?: VatRate
}

export interface ReceiptFee {
  id: string
  title: string
  /** What is charged: after `priceDiscount`. */
  price: number
  /** The price before `priceDiscount`. */
  priceOriginal: number
  /** Only the parking fee is discounted so far. */
  priceDiscount: Discount | null
  /**
   * Added and priced by the backend for a bike that isn't picked up. A
   * mechanic can discount it but not change its price or remove it.
   */
  isParkingFee: boolean
  /**
   * The fee's text. Shown under the parking fee, whose text says how long the
   * bike has waited; null where it isn't known.
   */
  description: string | null
  originalFeeId: string
  vatRate?: VatRate
}

export interface Receipt {
  packages: ReceiptPackage[]
  partActions: ReceiptPartAction[]
  manualActions: ReceiptManualAction[]
  wash: ReceiptWash | null
  fees: ReceiptFee[]
  vat: number
  totalCost: number
  /** Order-level discount (percent or kr), or null if no discount */
  orderDiscount: Discount | null
  /** Total cost after order discount applied, or null if no discount */
  totalCostAfterOrderDiscount: number | null
  /** Labor-level discount (percent or kr), or null if no discount */
  laborDiscount: Discount | null
  /** Total labor cost after discount, or null if no discount */
  laborCostAfterDiscount: number | null
  /** Products-level discount (percent or kr), or null if no discount */
  productsDiscount: Discount | null
  /** Total products cost after discount, or null if no discount */
  productsCostAfterDiscount: number | null
  customerBudget: number | null
  /** Total labor cost after labor-specific discount (before order discount). Includes wash and fees. */
  effectiveLaborCost: number
  /** Total products cost after product-specific discount (before order discount). */
  effectiveProductsCost: number
  /** The total discount amount given (in currency units, not percentage) */
  totalDiscountGiven: number
}

export const GetCombinedReceiptSchema = z.object({
  jobIds: z.array(z.guid()).min(1),
})
export type GetCombinedReceipt = z.infer<typeof GetCombinedReceiptSchema>

export interface CombinedReceiptJob {
  jobId: string
  displayNumber: number
  status: JobStatus
  /** Brand and model, falling back to the bike type label. */
  bikeLabel: string
  customerName: string
  /** E.g. "Totalt för stadscykel #71". */
  totalLabel: string
  /** The job's total after discounts. */
  totalToPay: number
  receipt: Receipt
}

export interface CombinedReceiptTotals {
  /** Sum of every job's products cost after product discounts. */
  productsCost: number
  /** Sum of every job's labor cost (incl. wash and fees) after labor discounts. */
  laborCost: number
  /** Sum of every job's order-level discount, in currency units. */
  orderDiscountGiven: number
  /** Sum of every job's total after all discounts. */
  totalToPay: number
}

export const combineReceiptTotals = (
  items: { receipt: Receipt; totalToPay: number }[],
): CombinedReceiptTotals => {
  // `applyDiscountsToReceipt` leaves `effective*` at the pre-discount amount
  // (the proposal subtotal relies on it), so prefer the after-discount fields.
  const productsCost = items.reduce(
    (sum, item) =>
      sum +
      (item.receipt.productsCostAfterDiscount ??
        item.receipt.effectiveProductsCost),
    0,
  )
  const laborCost = items.reduce(
    (sum, item) =>
      sum +
      (item.receipt.laborCostAfterDiscount ?? item.receipt.effectiveLaborCost),
    0,
  )
  const totalToPay = items.reduce((sum, item) => sum + item.totalToPay, 0)
  return {
    productsCost,
    laborCost,
    orderDiscountGiven: productsCost + laborCost - totalToPay,
    totalToPay,
  }
}

/** Receipts for several jobs, shown together with one shared total. */
export interface CombinedReceipt extends CombinedReceiptTotals {
  jobs: CombinedReceiptJob[]
  allDelivered: boolean
}
